import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

function Avatar({ person, size = 'md' }) {
  const sizeClass = size === 'sm'
    ? 'w-5 h-5 text-[9px]'
    : 'w-8 h-8 text-xs';

  const initials = (person.full_name || person.email || '?')
    .trim()
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div
      className={`${sizeClass} rounded-full ring-2 ring-white dark:ring-slate-900 overflow-hidden bg-emerald-100 flex-shrink-0 flex items-center justify-center`}
    >
      {person.profile_photo ? (
        <img
          src={person.profile_photo}
          alt={person.full_name}
          className="w-full h-full object-cover"
          referrerPolicy="no-referrer"
        />
      ) : (
        <span className="font-semibold text-emerald-700 leading-none">{initials}</span>
      )}
    </div>
  );
}

/**
 * Compact collaborators row shown below the estate name.
 * Shows all co-managers on the estate (everyone except the current user).
 * Renders nothing if the current user is the sole manager.
 */
export function PresenceAvatars({ presenceList }) {
  if (!presenceList.length) return null;

  const visible = presenceList.slice(0, 5);
  const overflow = presenceList.length - 5;

  return (
    <TooltipProvider>
      <div className="flex items-center justify-end gap-2 mt-1.5">
        <div className="flex -space-x-2">
          {visible.map((person) => (
            <Tooltip key={person.user_id}>
              <TooltipTrigger asChild>
                <div className="cursor-default">
                  <Avatar person={person} />
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p className="font-medium">{person.full_name || 'Manager'}</p>
                <p className="text-xs text-muted-foreground capitalize">{person.role}</p>
              </TooltipContent>
            </Tooltip>
          ))}
          {overflow > 0 && (
            <div className="w-8 h-8 rounded-full ring-2 ring-white dark:ring-slate-900 bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-semibold text-slate-600 dark:text-slate-300">
              +{overflow}
            </div>
          )}
        </div>
        <span className="text-xs text-slate-400 dark:text-slate-500">
          {presenceList.length === 1
            ? `1 collaborator`
            : `${presenceList.length} collaborators`}
        </span>
      </div>
    </TooltipProvider>
  );
}
